import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, LogOut, RefreshCw, Lock, Mail, Phone, Calendar, Users, MessageSquare, ExternalLink } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "dali_admin_token";

function LoginForm({ onLogin }) {
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/verify`, {}, { headers: { "X-Admin-Token": token } });
      localStorage.setItem(TOKEN_KEY, token);
      toast.success("Вход выполнен");
      onLogin(token);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Неверный токен");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#1A2035] px-6">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-md bg-[#FAF7F2] rounded-lg p-8 shadow-xl"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="rounded-full bg-[#1A2035] text-[#F5C58C] p-2.5">
            <Lock className="h-5 w-5" />
          </div>
          <div>
            <p className="overline text-[#D0533C]">Административная зона</p>
            <h1 className="font-display text-2xl mt-1">Вход по токену</h1>
          </div>
        </div>
        <label className="block text-[13px] font-medium text-foreground/80 mb-2">
          Токен администратора
        </label>
        <input
          data-testid="admin-token-input"
          type="password"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          placeholder="X-Admin-Token"
          autoFocus
          className="w-full rounded-md border border-border bg-white px-4 py-3 text-[14px] focus:outline-none focus:border-[#F07E26]"
        />
        <button
          data-testid="admin-login-btn"
          type="submit"
          disabled={busy || !token}
          className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#F07E26] text-white px-6 py-3 text-[14px] font-semibold hover:bg-[#d86a15] disabled:opacity-50"
        >
          {busy ? "Проверка..." : "Войти"}
        </button>
        <p className="mt-4 text-[12px] text-muted-foreground text-center">
          Токен настраивается через переменную <code>ADMIN_TOKEN</code> на сервере.
        </p>
      </form>
    </div>
  );
}

function StatCard({ label, value, testId }) {
  return (
    <div data-testid={testId} className="bg-white rounded-lg border border-border p-5">
      <p className="overline text-muted-foreground">{label}</p>
      <p className="font-display text-3xl mt-2">{value}</p>
    </div>
  );
}

function BookingsTable({ token, onLogout }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/bookings?limit=500`, {
        headers: { "X-Admin-Token": token },
      });
      setBookings(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Сессия истекла, войдите снова");
        onLogout();
      } else {
        toast.error("Ошибка загрузки заявок");
      }
    } finally {
      setLoading(false);
    }
  };

  const deleteBooking = async (id) => {
    if (!confirm("Удалить эту заявку?")) return;
    try {
      await axios.delete(`${API}/bookings/${id}`, { headers: { "X-Admin-Token": token } });
      setBookings((prev) => prev.filter((b) => b.id !== id));
      toast.success("Заявка удалена");
    } catch (err) {
      toast.error("Не удалось удалить");
    }
  };

  useEffect(() => {
    fetchBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const today = new Date().toISOString().slice(0, 10);
  const todayCount = bookings.filter((b) => (b.created_at || "").slice(0, 10) === today).length;
  const emailedCount = bookings.filter((b) => b.email_sent).length;

  return (
    <div className="min-h-screen bg-[#EBE5D9]">
      <header className="bg-[#1A2035] text-[#FAF7F2] py-5">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 flex items-center justify-between">
          <div>
            <p className="overline text-[#F5C58C]">Панель управления</p>
            <h1 className="font-display text-2xl mt-1">Дели с Али — Заявки</h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-refresh-btn"
              onClick={fetchBookings}
              className="inline-flex items-center gap-2 rounded-full border border-[#F5C58C]/40 text-[#FAF7F2] px-4 py-2 text-[13px] hover:bg-[#2A3352] transition"
            >
              <RefreshCw className="h-4 w-4" /> Обновить
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={onLogout}
              className="inline-flex items-center gap-2 rounded-full bg-[#F07E26] text-white px-4 py-2 text-[13px] font-semibold hover:bg-[#d86a15] transition"
            >
              <LogOut className="h-4 w-4" /> Выйти
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 py-10">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-8">
          <StatCard label="Всего заявок" value={bookings.length} testId="stat-total" />
          <StatCard label="За сегодня" value={todayCount} testId="stat-today" />
          <StatCard label="С email-уведомлением" value={emailedCount} testId="stat-emailed" />
        </div>

        {loading ? (
          <p className="text-center py-16 text-muted-foreground">Загрузка...</p>
        ) : bookings.length === 0 ? (
          <div className="bg-white rounded-lg border border-border py-16 text-center">
            <p className="font-display text-xl">Пока нет заявок</p>
            <p className="mt-2 text-sm text-muted-foreground">
              Новые заявки появятся здесь автоматически.
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]" data-testid="bookings-table">
                <thead className="bg-[#1A2035] text-[#FAF7F2] text-left">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Дата заявки</th>
                    <th className="px-4 py-3 font-semibold">Клиент</th>
                    <th className="px-4 py-3 font-semibold">Контакты</th>
                    <th className="px-4 py-3 font-semibold">Экскурсия</th>
                    <th className="px-4 py-3 font-semibold">Дата тура</th>
                    <th className="px-4 py-3 font-semibold">Чел.</th>
                    <th className="px-4 py-3 font-semibold">Комментарий</th>
                    <th className="px-4 py-3 font-semibold">Email</th>
                    <th className="px-4 py-3 font-semibold text-right">Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((b) => (
                    <tr key={b.id} data-testid={`booking-row-${b.id}`} className="border-t border-border hover:bg-[#FAF7F2]">
                      <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">
                        {new Date(b.created_at).toLocaleString("ru-RU")}
                      </td>
                      <td className="px-4 py-3 font-semibold">{b.name}</td>
                      <td className="px-4 py-3 text-muted-foreground">
                        <a href={`tel:${b.phone}`} className="flex items-center gap-1.5 hover:text-[#D0533C]">
                          <Phone className="h-3 w-3" /> {b.phone}
                        </a>
                        <a href={`mailto:${b.email}`} className="flex items-center gap-1.5 hover:text-[#D0533C] mt-1">
                          <Mail className="h-3 w-3" /> {b.email}
                        </a>
                      </td>
                      <td className="px-4 py-3">
                        <a
                          href={`/tours/${b.tour_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-[#D0533C] hover:underline"
                        >
                          {b.tour_title}
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="h-3 w-3 text-[#F07E26]" /> {b.date}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1">
                          <Users className="h-3 w-3 text-[#F07E26]" /> {b.people}
                        </span>
                      </td>
                      <td className="px-4 py-3 max-w-[220px]">
                        {b.comment ? (
                          <span className="flex items-start gap-1 text-muted-foreground">
                            <MessageSquare className="h-3 w-3 mt-0.5 shrink-0" />
                            <span className="line-clamp-2">{b.comment}</span>
                          </span>
                        ) : (
                          <span className="text-muted-foreground/50">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {b.email_sent ? (
                          <span className="inline-block px-2 py-0.5 rounded-full bg-[#2E7D5B]/15 text-[#2E7D5B] text-[11px] font-semibold">
                            Отправлено
                          </span>
                        ) : (
                          <span className="inline-block px-2 py-0.5 rounded-full bg-[#D0533C]/15 text-[#D0533C] text-[11px] font-semibold">
                            Не отправлено
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          data-testid={`delete-booking-${b.id}`}
                          onClick={() => deleteBooking(b.id)}
                          className="inline-flex items-center gap-1 text-[#D0533C] hover:text-[#a63e28] text-[12px] font-semibold"
                        >
                          <Trash2 className="h-3.5 w-3.5" /> Удалить
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  useEffect(() => {
    document.title = "Админ · Дели с Али";
  }, []);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  if (!token) return <LoginForm onLogin={setToken} />;
  return <BookingsTable token={token} onLogout={logout} />;
}
